import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, KeyRound, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForcePasswordChangePage = ({ token, user, organization, onPasswordChanged }) => {
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.newPassword || !formData.confirmPassword) {
            toast.error('Bitte füllen Sie alle Felder aus');
            return;
        }
        
        if (formData.newPassword.length < 6) {
            toast.error('Das Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        
        if (formData.newPassword !== formData.confirmPassword) {
            toast.error('Die Passwörter stimmen nicht überein');
            return;
        }

        setLoading(true);
        
        try {
            await axios.post(
                `${API}/auth/change-password`,
                { new_password: formData.newPassword },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success('Passwort erfolgreich geändert');
            onPasswordChanged();
            
        } catch (error) {
            console.error('Password change failed:', error);
            toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
            <div className="w-full max-w-md">
                {/* Warning Header */}
                <div className="text-center mb-8">
                    <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
                        <ShieldAlert className="h-8 w-8 text-amber-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">
                        Passwort ändern erforderlich
                    </h1>
                    <p className="text-slate-400">
                        Ihr Passwort wurde zurückgesetzt. Bitte legen Sie ein neues Passwort fest.
                    </p>
                </div>

                <Card className="border-0 shadow-2xl bg-slate-800 border-slate-700">
                    <CardHeader className="space-y-1 pb-4">
                        <CardTitle className="text-xl text-white">Neues Passwort festlegen</CardTitle>
                        <CardDescription className="text-slate-400">
                            Angemeldet als: <span className="text-white">{user?.email}</span>
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="newPassword" className="text-slate-200">Neues Passwort</Label>
                                <Input
                                    id="newPassword"
                                    type="password"
                                    placeholder="Mindestens 6 Zeichen"
                                    value={formData.newPassword}
                                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                    disabled={loading}
                                    className="bg-slate-700 border-slate-600 text-white"
                                    data-testid="new-password-input"
                                    autoComplete="new-password"
                                    minLength={6}
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="text-slate-200">Passwort bestätigen</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="Passwort wiederholen"
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                    disabled={loading}
                                    className="bg-slate-700 border-slate-600 text-white"
                                    data-testid="confirm-password-input"
                                    autoComplete="new-password"
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-amber-600 hover:bg-amber-700"
                                disabled={loading}
                                data-testid="change-password-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Speichern...
                                    </>
                                ) : (
                                    <>
                                        <KeyRound className="mr-2 h-4 w-4" />
                                        Passwort ändern
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <p className="text-center text-sm text-slate-500 mt-6">
                    Nach dem Ändern werden Sie automatisch weitergeleitet.
                </p>
            </div>
        </div>
    );
};
